/**
 * **P-025, second half** — the board toolbar every *non-table* view wears.
 *
 * Measured on the account's own kanban and gantt
 * (`discovery/ui-reference/parity/174316936/view-5815833-kanban-monday.png`,
 * `…/view-1756489-gantt-monday.png`): monday keeps the whole bar on every view, in one order —
 *
 *   `▥ Kanban ▾` │ `New item ▾` · `Search` · `Person` · `Filter ▾` · `Sort` · `⋯` · (the view's
 *   own controls) · the settings gear
 *
 * — while ours showed a single ⚙ at the top right and nothing else, on kanban, timeline,
 * calendar, gallery and workload alike.
 *
 * It is one component rather than one per kind, and which controls it draws comes from the
 * **registry** (`supportsFilters` / `supportsSort`), never from an `if (kind === …)`. The table
 * keeps its own `board-view/table/Toolbar.tsx`, because that one is wired to the table store;
 * the two share `table.css`, so the band is the same 32 px at the same 38 px inset on every
 * view and P-024's header-stack work lands on both at once.
 *
 * **Why a hook *and* a component.** What the bar drives is `QueryState` — the search, the
 * person shortcut, the quick filter and the sort — and a view has to hand that to
 * `useViewData` *before* it has a payload to render the bar from. So `useViewQueryControls()`
 * owns the state at the top of a view and `<BoardViewToolbar>` paints it further down, with the
 * columns, people and facets the read came back with. No view filters rows in the browser: a
 * filter set here is the same server-side read the grid does.
 */
import { viewTypeRegistry, type ColumnId } from '@elizmonday/shared';
import { Icon, icons, useOverlayDismiss } from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { getColumnUi } from '../../board-view/columns/registry';
import type {
  BoardColumn,
  BoardDataSource,
  BoardViewPayload,
  FacetValue,
  QueryState,
} from '../../board-view/data/types';
import {
  BLANK_FACET,
  countRules,
  quickFilterToGroup,
  type FilterGroup,
  type SortRule,
} from '../../board-view/table/settings';
import { useBoardViews } from '../../boards/hooks';
import { boardViewUrl } from '../../shell/urls';
import { ViewBarPane } from './ViewBarPane';
import '../../board-view/table/table.css';
import './view-toolbar.css';

/** The columns the `Person` control filters on — the table's own list (`PersonFilter.tsx`). */
const PEOPLE_LIKE = new Set(['people', 'person', 'team', 'item_assignees']);

/** Measured: 10 facet values per column before the pane pages (`B6 §4.2`). */
const FACET_PAGE = 10;

type PaneKind = 'kind' | 'new' | 'person' | 'filter' | 'sort' | 'more' | null;

export interface ViewQueryControls {
  readonly pane: PaneKind;
  setPane(next: PaneKind): void;
  readonly searching: boolean;
  setSearching(next: boolean): void;
  readonly search: string;
  setSearch(next: string): void;
  /** The quick-filter selection, in the table pane's own shape (`quickFilterToGroup`). */
  readonly selection: ReadonlyMap<ColumnId, readonly unknown[]>;
  toggleFacet(columnId: ColumnId, value: unknown): void;
  readonly people: readonly string[];
  setPeople(next: readonly string[]): void;
  readonly sort: readonly SortRule[];
  toggleSort(columnId: ColumnId): void;
  clear(): void;
  /** Which people-like columns the person shortcut filters on; the bar sets it once it has them. */
  setPersonColumns(ids: readonly ColumnId[]): void;
}

export interface ViewQueryState {
  /** Spread into the view's `useViewData({ state })`. */
  readonly state: Partial<QueryState>;
  readonly controls: ViewQueryControls;
}

/**
 * The bar's state, owned at the top of a view so its `QueryState` can reach `useViewData`
 * before the bar itself has anything to render.
 */
export function useViewQueryControls(): ViewQueryState {
  const [pane, setPane] = useState<PaneKind>(null);
  const [searching, setSearching] = useState(false);
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<ReadonlyMap<ColumnId, readonly unknown[]>>(new Map());
  const [people, setPeople] = useState<readonly string[]>([]);
  const [personColumns, setPersonColumns] = useState<readonly ColumnId[]>([]);
  const [sort, setSort] = useState<readonly SortRule[]>([]);

  const toggleFacet = useCallback((columnId: ColumnId, value: unknown) => {
    setSelection((current) => {
      const next = new Map<ColumnId, readonly unknown[]>(current);
      const existing = next.get(columnId) ?? [];
      const key = String(value);
      const without = existing.filter((entry) => String(entry) !== key);
      next.set(columnId, without.length === existing.length ? [...existing, value] : without);
      return next;
    });
  }, []);

  const toggleSort = useCallback((columnId: ColumnId) => {
    setSort((current) => {
      const existing = current.find((rule) => rule.columnId === columnId);
      if (existing === undefined) return [...current, { columnId, direction: 'asc' as const }];
      if (existing.direction === 'asc') {
        return current.map((rule) => (rule.columnId === columnId ? { ...rule, direction: 'desc' as const } : rule));
      }
      return current.filter((rule) => rule.columnId !== columnId);
    });
  }, []);

  const clear = useCallback(() => {
    setSelection(new Map());
    setPeople([]);
  }, []);

  /**
   * The person shortcut is an ordinary `FilterGroup` (`PersonFilter.tsx`): `any_of` over every
   * people-like column, OR-ed — AND-ed with the quick-filter selection when both are set, which
   * is what the table does.
   */
  const filter = useMemo<FilterGroup | null>(() => {
    const quick = quickFilterToGroup(selection);
    const person: FilterGroup | null =
      people.length === 0 || personColumns.length === 0
        ? null
        : {
            operator: 'or',
            rules: personColumns.map((columnId) => ({
              columnId,
              operator: 'any_of' as const,
              compareValue: people.map(Number),
            })),
            groups: [],
          };
    if (quick === null) return person;
    if (person === null) return quick;
    return { operator: 'and', rules: [], groups: [quick, person] };
  }, [selection, people, personColumns]);

  const state = useMemo<Partial<QueryState>>(
    () => ({
      ...(search === '' ? {} : { search }),
      ...(filter === null ? {} : { filter }),
      ...(sort.length === 0 ? {} : { sort }),
    }),
    [search, filter, sort],
  );

  return {
    state,
    controls: {
      pane, setPane, searching, setSearching, search, setSearch,
      selection, toggleFacet, people, setPeople, sort, toggleSort, clear, setPersonColumns,
    },
  };
}

export interface BoardViewToolbarProps {
  controls: ViewQueryControls;
  boardId: string;
  viewId: string;
  /** The registry id of the open view — `kanban`, `gantt`, … It decides which controls show. */
  registryId: string;
  source: BoardDataSource | null;
  /** The view's own payload: columns and people. */
  payload: BoardViewPayload | null;
  /**
   * The groups `New item` may add to. A grouped read hands back monday's virtual `gb:` buckets,
   * which are not groups anybody can write to, so a view that reads grouped (the kanban) passes
   * its **ungrouped** probe's groups instead and the rest pass their own.
   */
  groups?: readonly { readonly id: string }[];
  /** Called after `New item` or `New group` wrote, so the view re-reads. */
  onChanged?: () => void;
  /** The view's own controls — Baseline, Auto Fit, the zoom stepper — in monday's own order. */
  extras?: ReactNode;
  /** The kind's settings gear (`ViewToolbar`), at the right end of the bar. */
  gear?: ReactNode;
}

export function BoardViewToolbar({
  controls,
  boardId,
  viewId,
  registryId,
  source,
  payload,
  groups,
  onChanged,
  extras,
  gear,
}: BoardViewToolbarProps) {
  const definition = viewTypeRegistry.find(registryId);
  const navigate = useNavigate();
  /**
   * T-8d: an **offline** source (the `/__ui/*` fixture harnesses) already carries the board's
   * view tabs in its payload, and its board id is not one the api can answer for — asking
   * `views.list` there produced a 404 on every harness run. The product route keeps the query.
   */
  const views = useBoardViews(source?.offline === true ? null : boardId);
  const [facets, setFacets] = useState<Record<string, FacetValue[]>>({});
  /**
   * The bar's leading control, and the anchor every pane on it is placed against — `.ez-toolbar`
   * has `padding: 0 38px`, so this button's left edge *is* 8b's measured `inset-inline-start: 38px`
   * and its bottom edge is 4 px above the pane's measured top (`ViewBarPane`).
   */
  const [leadControl, setLeadControl] = useState<HTMLButtonElement | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const { pane, setPane, setPersonColumns } = controls;

  const viewTabs: readonly { readonly id: string | null; readonly name: string }[] = payload?.views ?? views.data ?? [];
  const openView = viewTabs.find((row) => String(row.id) === viewId);
  /**
   * **P-152.** *Open the main table* used to navigate to `/boards/:id`, which is `BoardRedirect`
   * — and that resolves a board to its **last viewed** view before its default, i.e. to the view
   * the row was just clicked from. The one row whose whole purpose is to leave the view was a
   * round trip. It navigates to the board's `table` view **by id** instead; the bare board url
   * stays the fallback for a board with no table row at all (the implicit main table, whose tab
   * is `id: null`), and `BoardRedirect`'s last-viewed rule — which is right for opening a board
   * from the sidebar — does not change.
   */
  const mainTableViewId = useMemo(() => {
    const rows: readonly { id: string | null; registryId?: string | null }[] = views.data ?? [];
    return rows.find((row) => row.registryId === 'table')?.id ?? null;
  }, [views.data]);
  const columns = useMemo(() => payload?.columns ?? [], [payload]);
  const personColumnIds = useMemo(
    () => columns.filter((column) => PEOPLE_LIKE.has(column.type)).map((column) => column.id),
    [columns],
  );
  useEffect(() => setPersonColumns(personColumnIds), [personColumnIds, setPersonColumns]);

  /** The facets the filter pane offers, read when it opens — one `GROUP BY` per column. */
  useEffect(() => {
    if (pane !== 'filter' || source === null || columns.length === 0) return undefined;
    let cancelled = false;
    void source
      .facets({ boardId: boardId as never, viewId: viewId as never })
      .then((next) => {
        if (!cancelled) setFacets(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [pane, source, boardId, viewId, columns.length]);

  useOverlayDismiss({ open: pane !== null, onClose: () => setPane(null) });

  const firstGroup = (groups ?? payload?.groups ?? []).find((group) => !group.id.startsWith('gb:'));
  const itemTerm = payload?.board.itemTerminology ?? 'item';
  const filterCount = countRules(quickFilterToGroup(controls.selection)) + (controls.people.length > 0 ? 1 : 0);

  const addItem = () => {
    if (source === null || firstGroup === undefined) return;
    void source
      .createItem({
        boardId: boardId as never,
        groupId: firstGroup.id as never,
        name: `New ${itemTerm}`,
        placement: 'top',
      })
      .then(() => onChanged?.())
      .catch(() => undefined);
  };

  const control = (id: string, label: string, icon: keyof typeof icons, target: PaneKind, badge = 0) => (
    <button
      type="button"
      className="ez-toolbar__button"
      data-testid={`toolbar-${id}`}
      data-active={pane === target || badge > 0 || undefined}
      data-count={badge > 0 ? badge : undefined}
      aria-pressed={pane === target}
      onClick={() => setPane(pane === target ? null : target)}
    >
      <Icon icon={icons[icon]} size={16} ignoreFocusStyle />
      {/* Measured: monday renames the control rather than adding a chip — `Filter / 2`. */}
      <span>{badge > 0 ? `${label} / ${String(badge)}` : label}</span>
    </button>
  );

  return (
    <div className="ez-toolbar ez-view-bar" role="toolbar" aria-label="Board actions" data-testid="board-toolbar">
      {/* monday's left-hand kind selector: the open view's kind, and a menu of the board's views. */}
      <button
        type="button"
        ref={setLeadControl}
        className="ez-view-bar__kind"
        data-testid="toolbar-view-kind"
        aria-expanded={pane === 'kind'}
        onClick={() => setPane(pane === 'kind' ? null : 'kind')}
      >
        <Icon icon={icons.board} size={16} ignoreFocusStyle />
        {/*
          monday names the **open view**, not its kind: the selector over board `174316936`'s
          `TimelineGanttBoardView` reads `Timeline ▾`, which is that view row's name. The kind's
          registry title is the fallback for a tab we have not loaded yet.
        */}
        <span>{openView?.name ?? definition?.title ?? 'View'}</span>
        <Icon icon={icons.chevronDown} size={14} ignoreFocusStyle />
      </button>

      <div className="ez-toolbar__new">
        <button
          type="button"
          className="ez-toolbar__primary"
          data-testid="new-item"
          disabled={firstGroup === undefined}
          onClick={addItem}
        >
          {`New ${itemTerm}`}
        </button>
        <button
          type="button"
          className="ez-toolbar__primary-caret"
          aria-label="New item options"
          data-testid="new-item-options"
          aria-expanded={pane === 'new'}
          onClick={() => setPane(pane === 'new' ? null : 'new')}
        >
          <Icon icon={icons.chevronDown} size={14} ignoreFocusStyle />
        </button>
      </div>

      {/* Measured: `Search` does not open a pane — the control itself becomes the input. */}
      {controls.searching || controls.search !== '' ? (
        <input
          className="ez-view-bar__search"
          type="search"
          aria-label="Search this board"
          placeholder="Search this board"
          data-testid="toolbar-search-input"
          value={controls.search}
          onChange={(event) => controls.setSearch(event.target.value)}
          onBlur={() => controls.setSearching(false)}
        />
      ) : (
        <button
          type="button"
          className="ez-toolbar__button"
          data-testid="toolbar-search"
          onClick={() => controls.setSearching(true)}
        >
          <Icon icon={icons.search} size={16} ignoreFocusStyle />
          <span>Search</span>
        </button>
      )}

      {control('person', 'Person', 'person', 'person', controls.people.length)}
      {definition?.supportsFilters === false ? null : control('filter', 'Filter', 'filter', 'filter', filterCount)}
      {definition?.supportsSort === false ? null : control('sort', 'Sort', 'sort', 'sort', controls.sort.length)}

      <button
        type="button"
        className="ez-toolbar__icon"
        aria-label="Board options"
        data-testid="toolbar-more"
        aria-expanded={pane === 'more'}
        onClick={() => setPane(pane === 'more' ? null : 'more')}
      >
        <Icon icon={icons.moreActions} size={16} ignoreFocusStyle />
      </button>

      {extras}

      {/* The kind's own settings gear closes the bar on the right, where monday's does. */}
      <div className="ez-view-bar__end">{gear}</div>

      {pane === 'kind' ? (
        <ViewBarPane anchor={leadControl} label="Views" onClose={() => setPane(null)} testId="toolbar-view-kind-menu">
          {(views.data ?? []).map((row) => (
            <button
              type="button"
              key={row.id ?? 'main'}
              className="ez-view-bar__row"
              data-testid={`toolbar-view-${row.id ?? 'main'}`}
              onClick={() => {
                setPane(null);
                navigate(row.id === null ? `/boards/${boardId}` : boardViewUrl(boardId, row.id));
              }}
            >
              {row.name}
            </button>
          ))}
        </ViewBarPane>
      ) : null}

      {pane === 'new' ? (
        <ViewBarPane anchor={leadControl} label="New item options" onClose={() => setPane(null)} testId="new-item-menu">
          <button
            type="button"
            className="ez-view-bar__row"
            data-testid="new-group"
            onClick={() => {
              setPane(null);
              if (source !== null) {
                void source.createGroup({ boardId: boardId as never }).then(() => onChanged?.());
              }
            }}
          >
            New group of items
          </button>
          <button
            type="button"
            className="ez-view-bar__row"
            disabled
            title="Importing items from a file arrives with M12; the account migration importer is admin-only."
          >
            Import items
          </button>
        </ViewBarPane>
      ) : null}

      {pane === 'person' ? (
        <ViewBarPane anchor={leadControl} label="Filter this board by person" onClose={() => setPane(null)} testId="person-pane">
          {(payload?.people ?? []).map((person) => (
            <label className="ez-view-bar__row" key={person.id}>
              <input
                type="checkbox"
                checked={controls.people.includes(person.id)}
                data-testid={`person-${person.id}`}
                onChange={(event) =>
                  controls.setPeople(
                    event.target.checked
                      ? [...controls.people, person.id]
                      : controls.people.filter((id) => id !== person.id),
                  )
                }
              />
              <span>{person.name}</span>
            </label>
          ))}
        </ViewBarPane>
      ) : null}

      {pane === 'filter' ? (
        <ViewBarPane anchor={leadControl} label="Quick filters" onClose={() => setPane(null)} testId="quick-filter-pane" wide>
          <div className="ez-view-bar__facets">
            {columns
              .filter((column) => (facets[column.id]?.length ?? 0) > 0)
              .map((column) => (
                <FacetColumn
                  key={column.id}
                  column={column}
                  values={facets[column.id] ?? []}
                  picked={controls.selection.get(column.id) ?? []}
                  expanded={expanded.has(column.id)}
                  onExpand={() => setExpanded(new Set([...expanded, column.id]))}
                  onToggle={(value) => controls.toggleFacet(column.id, value)}
                />
              ))}
          </div>
          <button
            type="button"
            className="ez-view-bar__clear"
            data-testid="quick-filter-clear"
            disabled={filterCount === 0}
            onClick={() => controls.clear()}
          >
            Clear all
          </button>
        </ViewBarPane>
      ) : null}

      {pane === 'sort' ? (
        <ViewBarPane anchor={leadControl} label="Sort" onClose={() => setPane(null)} testId="sort-pane">
          {columns.map((column) => {
            const rule = controls.sort.find((entry) => entry.columnId === column.id);
            return (
              <button
                type="button"
                key={column.id}
                className="ez-view-bar__row"
                data-testid={`sort-${column.id}`}
                data-direction={rule?.direction}
                onClick={() => controls.toggleSort(column.id)}
              >
                <Icon icon={icons[getColumnUi(column.type).icon]} size={16} ignoreFocusStyle />
                <span>{column.title}</span>
                <span className="ez-view-bar__direction">
                  {rule === undefined ? '' : rule.direction === 'asc' ? '↑' : '↓'}
                </span>
              </button>
            );
          })}
        </ViewBarPane>
      ) : null}

      {pane === 'more' ? (
        <ViewBarPane anchor={leadControl} label="Board options" onClose={() => setPane(null)} testId="toolbar-more-menu">
          <button
            type="button"
            className="ez-view-bar__row"
            data-testid="more-open-table"
            onClick={() => {
              setPane(null);
              navigate(mainTableViewId === null ? `/boards/${boardId}` : boardViewUrl(boardId, mainTableViewId));
            }}
          >
            Open the main table
          </button>
          <button
            type="button"
            className="ez-view-bar__row"
            data-testid="more-activity"
            onClick={() => {
              setPane(null);
              navigate(`${boardViewUrl(boardId, viewId)}/activity_log`);
            }}
          >
            Activity log
          </button>
        </ViewBarPane>
      ) : null}
    </div>
  );
}

/** One column of facet values inside the quick-filter pane, 10 at a time (`B6 §4.2`). */
function FacetColumn({
  column,
  values,
  picked,
  expanded,
  onExpand,
  onToggle,
}: {
  column: BoardColumn;
  values: readonly FacetValue[];
  picked: readonly unknown[];
  expanded: boolean;
  onExpand(): void;
  onToggle(value: unknown): void;
}) {
  const shown = expanded ? values : values.slice(0, FACET_PAGE);
  return (
    <section className="ez-view-bar__facet" data-testid={`facet-${column.id}`}>
      <h4 className="ez-view-bar__facet-title">
        <Icon icon={icons[getColumnUi(column.type).icon]} size={14} ignoreFocusStyle />
        {column.title}
      </h4>
      {shown.map((facet) => {
        const value = facet.value === null ? BLANK_FACET : facet.value;
        const on = picked.some((entry) => String(entry) === String(value));
        return (
          <button
            type="button"
            key={String(value)}
            className="ez-view-bar__facet-value"
            data-active={on || undefined}
            data-testid={`facet-${column.id}-${String(value)}`}
            onClick={() => onToggle(value)}
          >
            <span>{facet.label}</span>
            <span className="ez-view-bar__facet-count">{facet.count}</span>
          </button>
        );
      })}
      {expanded || values.length <= FACET_PAGE ? null : (
        <button
          type="button"
          className="ez-view-bar__more"
          data-testid="view-bar-show-more"
          onClick={onExpand}
        >
          Show more
        </button>
      )}
    </section>
  );
}
